import React from 'react';
import { motion } from 'framer-motion';
import { 
  XMarkIcon, 
  BuildingOfficeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import Modal from '../../../components/common/Modal';

const PlanDetailsModal = ({ plan, isLoading = false, isOpen, onClose }) => {
  if (!plan) return null;

  // Handle both the detailed API response and the simple plan object
  const planData = plan.plan || plan;
  const subscriptions = plan.subscriptions || [];
  const stats = plan.stats || {};
  
  const planFeatures = planData.features || {};
  const moduleFeatures = planFeatures.modules || [];

  // Use stats from detailed response if available, otherwise fall back to plan properties
  const activeSubscriptions = stats.activeSubscriptions || plan.subscriptionCount || 0;
  const totalRevenue = stats.statusBreakdown?.find(s => s._id === 'active')?.revenue || plan.monthlyRevenue || 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="xl">
      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading plan details...</span>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-lg ${
                  planData.isActive ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  <CurrencyDollarIcon className={`w-8 h-8 ${
                    planData.isActive ? 'text-green-600' : 'text-gray-600'
                  }`} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{planData.name}</h2>
                  <p className="text-gray-600">{planData.description}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-blue-50 p-4 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Active Subscriptions</p>
                <p className="text-2xl font-bold text-blue-900">{activeSubscriptions}</p>
              </div>
              <BuildingOfficeIcon className="w-8 h-8 text-blue-600" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-green-50 p-4 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Monthly Revenue</p>
                <p className="text-2xl font-bold text-green-900">
                  {formatCurrency(totalRevenue, planData.currency)}
                </p>
              </div>
              <CurrencyDollarIcon className="w-8 h-8 text-green-600" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-purple-50 p-4 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Max Employees</p>
                <p className="text-2xl font-bold text-purple-900">
                  {planFeatures.maxEmployees || 'N/A'}
                </p>
              </div>
              <UserGroupIcon className="w-8 h-8 text-purple-600" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className={`p-4 rounded-lg ${
              planData.isActive ? 'bg-green-50' : 'bg-red-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${
                  planData.isActive ? 'text-green-600' : 'text-red-600'
                }`}>
                  Status
                </p>
                <p className={`text-2xl font-bold ${
                  planData.isActive ? 'text-green-900' : 'text-red-900'
                }`}>
                  {planData.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
              <CheckCircleIcon className={`w-8 h-8 ${
                planData.isActive ? 'text-green-600' : 'text-red-600'
              }`} />
            </div>
          </motion.div>
        </div>

        {/* Plan Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Pricing & Limits */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Price:</span>
                  <span className="font-semibold">
                    {formatCurrency(planData.price?.monthly || 0, planData.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Yearly Price:</span>
                  <span className="font-semibold">
                    {formatCurrency(planData.price?.yearly || 0, planData.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Currency:</span>
                  <span className="font-semibold">{planData.currency}</span>
                </div>
                {planData.trialDays > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Trial Period:</span>
                    <span className="font-semibold">{planData.trialDays} days</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Limits</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Max Employees:</span>
                  <span className="font-semibold">
                    {planFeatures.maxEmployees || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Max Branches:</span>
                  <span className="font-semibold">
                    {planFeatures.maxBranches || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Storage Limit:</span>
                  <span className="font-semibold">
                    {planFeatures.storage ? `${planFeatures.storage} GB` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Support Level:</span>
                  <span className="font-semibold capitalize">
                    {planFeatures.support || 'Basic'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Modules</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              {moduleFeatures.length > 0 ? (
                <div className="space-y-3">
                  {moduleFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No modules defined</p>
              )}
            </div>
          </div>
        </div>

     
        {/* Plan Availability */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Availability</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex flex-wrap gap-2">
              {planData.countries && planData.countries.length > 0 ? (
                planData.countries.map((country, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {country}
                  </span>
                ))
              ) : (
                <p className="text-gray-500">No countries specified</p>
              )}
            </div>
          </div>
        </div>

        {/* Subscription History */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Subscriptions</h3>
          <div className="bg-gray-50 rounded-lg overflow-hidden">
            {subscriptions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Billing Cycle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscriptions.slice(0, 5).map((subscription, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {subscription.companyId?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            subscription.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : subscription.status === 'expired'
                              ? 'bg-red-100 text-red-800'
                              : subscription.status === 'trial'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {subscription.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {subscription.billingCycle || 'monthly'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(subscription.price || 0, planData.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(subscription.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                No subscriptions found for this plan
              </div>
            )}
          </div>
        </div>

        {/* Plan Metadata */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Created</h4>
            <div className="flex items-center space-x-2 text-gray-600">
              <ClockIcon className="w-4 h-4" />
              <span className="text-sm">{formatDate(planData.createdAt)}</span>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Last Updated</h4>
            <div className="flex items-center space-x-2 text-gray-600">
              <ClockIcon className="w-4 h-4" />
              <span className="text-sm">{formatDate(planData.updatedAt)}</span>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </Modal>
  );
};

export default PlanDetailsModal;